import { useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'

/**
 * Largura de projeto (aspecto ~16:9): o conteúdo é desenhado nesta largura e
 * escalado pra caber. Larga o bastante pra encher a largura de uma TV/laptop sem
 * sobrar margem lateral.
 */
const LARGURA = 2500

/**
 * Desenha o conteúdo numa largura fixa e o escala para caber na tela inteira, sem
 * rolagem e sem cortar nada: mede a altura real do conteúdo e encolhe/cresce pra
 * caber na viewport. É o que permite montar um telão denso reusando componentes
 * com tamanho intrínseco — encolhe num laptop, cresce numa TV.
 */
export function PalcoFit({ children }: { children: ReactNode }): ReactElement {
  const areaRef = useRef<HTMLDivElement>(null)
  const conteudoRef = useRef<HTMLDivElement>(null)
  const [escala, setEscala] = useState(1)

  useLayoutEffect(() => {
    const area = areaRef.current!
    const conteudo = conteudoRef.current!
    function ajustar(): void {
      setEscala(Math.min(area.clientWidth / LARGURA, area.clientHeight / conteudo.offsetHeight))
    }
    ajustar()
    // Remede quando a viewport OU o conteúdo mudam de tamanho (fontes carregando,
    // troca de período recriando os cards) — sem isso a medida inicial fica velha.
    const observer = new ResizeObserver(ajustar)
    observer.observe(area)
    observer.observe(conteudo)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={areaRef} className="flex h-full w-full items-start justify-center overflow-hidden">
      <div
        ref={conteudoRef}
        className="shrink-0"
        style={{ width: LARGURA, transform: `scale(${escala})`, transformOrigin: 'top center' }}
      >
        {children}
      </div>
    </div>
  )
}
